import React, { useMemo, useCallback, useState } from 'react';
import {
  FAMILIES,
  GENERATORS,
  GENERATOR_MAP,
  DEFAULT_GENART_CONFIG,
  LEGACY_TYPE_MAP,
  getDefaultParams,
  getDefaultPalette,
} from '../../constants/genArt';
import { GenArtGeneratorGrid } from './genart/GenArtGeneratorGrid';
import { GenArtPalettePicker } from './genart/GenArtPalettePicker';
import { GenArtControls } from './genart/GenArtControls';
import { GenArtSeedControl } from './genart/GenArtSeedControl';
import { DarkenSlider, ToggleSwitch, ScopeSelector, CollapseTransition } from '../../sdk';

/**
 * Phase C+D: Complete rewrite of Generative Art editor.
 *
 * Canvas2D-based generative backgrounds with 20 generators, 41 palettes,
 * per-generator controls, and hardware instrument design language.
 *
 * Phase D additions:
 *   - "This Slide" / "All Slides" scope selector (LED variant)
 *   - "All Slides" uses sequential seeds: seed + slideIndex
 *
 * Sections:
 *   1. Header + Enable toggle + Scope selector
 *   2. Family selector (LED radio tabs)
 *   3. Generator grid (instrument buttons with thumbnails)
 *   4. Palette picker (color-well rows)
 *   5. Seed control (LCD readout + shuffle)
 *   6. Per-generator controls (dynamic sliders)
 *   7. Global color adjustments (hue, saturation, brightness)
 *   8. Darken slider
 */
export function GenerativeEditor({
  generative,
  onChange,
  slides = [],
  activeIndex = 0,
  onBulkSlideChange,
}) {
  // ── Normalize config (handle legacy SVG format) ──
  const config = useMemo(() => normalizeConfig(generative), [generative]);
  const enabled = config.enabled;

  // ── Scope state (This Slide / All Slides) ──
  const [genScope, setGenScope] = useState('slide');

  // ── View family — controls which family tab is shown (UI-only) ──
  // Separate from config.family so switching tabs doesn't change the active generator.
  const [viewFamily, setViewFamily] = useState(config.family);

  // Count unique generative configs across slides (for diversity indicator)
  const uniqueGenCount = useMemo(() => {
    const keys = new Set(
      slides.map(s => {
        if (!s.generative?.enabled) return '__off__';
        return `${s.generative.type || s.generative.preset || '?'}-${s.generative.paletteId || '?'}-${s.generative.seed || 0}`;
      })
    );
    return keys.size;
  }, [slides]);

  // ── Update helper — respects scope ──
  const update = useCallback((patch) => {
    const newConfig = { ...config, ...patch };
    const seedExplicitlyChanged = 'seed' in patch;

    if (genScope === 'all' && onBulkSlideChange) {
      // Apply to all slides — only reassign seeds if user explicitly changed the seed
      if (seedExplicitlyChanged) {
        const baseSeed = newConfig.seed ?? 42;
        onBulkSlideChange(
          (slideIndex) => ({
            generative: {
              ...newConfig,
              seed: baseSeed + slideIndex,
            },
          }),
          `Generative art applied to all slides`
        );
      } else {
        // Preserve each slide's existing seed
        onBulkSlideChange(
          (slideIndex, currentSlide) => ({
            generative: {
              ...newConfig,
              seed: currentSlide?.generative?.seed ?? newConfig.seed,
            },
          }),
          `Generative art applied to all slides`
        );
      }
    } else {
      onChange(newConfig);
    }
  }, [config, onChange, genScope, onBulkSlideChange]);

  // ── Family change handler — UI navigation only, does NOT change the active generator ──
  const handleFamilyChange = useCallback((familyId) => {
    setViewFamily(familyId);
  }, []);

  // ── Generator change handler — this is the explicit user action ──
  const handleGeneratorChange = useCallback((typeId) => {
    const gen = GENERATOR_MAP[typeId];
    if (!gen) return;

    const patch = {
      type: typeId,
      family: gen.family,
      params: getDefaultParams(typeId),
    };

    // If switching families, also update palette to one that fits
    if (gen.family !== config.family) {
      patch.paletteId = getDefaultPalette(gen.family);
    }

    update(patch);
  }, [config.family, update]);

  // ── Param change handler ──
  const handleParamChange = useCallback((key, value) => {
    update({
      params: { ...config.params, [key]: value },
    });
  }, [config.params, update]);

  // ── Global change handler ──
  const handleGlobalChange = useCallback((key, value) => {
    update({ [key]: value });
  }, [update]);

  return (
    <div className="field-container">
      {/* ── Header + Scope + Enable toggle — single row ── */}
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <div className="module-header">Generative Background</div>
          <div className="module-desc">Canvas2D procedural art — 21 generators.</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {enabled && slides.length > 1 && onBulkSlideChange && (
            <>
              <ScopeSelector
                variant="led"
                scope={genScope}
                onScopeChange={setGenScope}
                uniqueCount={uniqueGenCount}
                diversityNoun="generative configs"
                confirmMessage={`Slides use ${uniqueGenCount} different generative settings. Apply current config to all ${slides.length} slides with sequential seeds?`}
                onConfirmApply={() => {
                  const baseSeed = config.seed ?? 42;
                  if (onBulkSlideChange) {
                    onBulkSlideChange(
                      (slideIndex) => ({
                        generative: {
                          ...config,
                          seed: baseSeed + slideIndex,
                        },
                      }),
                      `Generative art applied to all slides`
                    );
                  }
                }}
              />
              <div className="w-px h-5 rounded-full" style={{
                boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.5), 1px 0 0 rgba(255,255,255,0.07)',
              }} />
            </>
          )}
          <ToggleSwitch
            checked={enabled}
            onChange={(v) => update({ enabled: v })}
          />
        </div>
      </div>

      <CollapseTransition open={enabled}>
        <div className="space-y-4">
          {/* ── Family Selector (instrument buttons like font presets) ── */}
          <div>
            <div className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">Family</div>
            <div className="genart-family-grid">
              {FAMILIES.map(fam => {
                const isActive = fam.id === viewFamily;
                return (
                  <button
                    key={fam.id}
                    className={`genart-family-btn${isActive ? ' genart-family-btn-active' : ''}`}
                    onClick={() => handleFamilyChange(fam.id)}
                  >
                    <div
                      className={`genart-family-led${isActive ? ' genart-family-led-active' : ''}`}
                      style={isActive ? {
                        background: fam.accent,
                        '--fam-led-glow': fam.accentGlow,
                      } : undefined}
                    />
                    <span
                      className={`genart-family-label${isActive ? ' genart-family-label-active' : ''}`}
                      style={isActive ? {
                        color: fam.accent,
                        '--fam-label-glow': fam.accentGlow,
                      } : undefined}
                    >
                      {fam.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Generator Grid ── */}
          <GenArtGeneratorGrid
            family={viewFamily}
            selectedType={config.type}
            paletteId={config.paletteId}
            seed={config.seed}
            onSelect={handleGeneratorChange}
          />

          {/* ── Palette Picker ── */}
          <GenArtPalettePicker
            family={viewFamily}
            selectedPaletteId={config.paletteId}
            onSelect={(id) => update({ paletteId: id })}
          />

          {/* ── Seed Control (hidden for Normies — Normie ID is the only control) ── */}
          {viewFamily !== 'normies' && (
            <GenArtSeedControl
              seed={config.seed}
              onChange={(seed) => update({ seed })}
            />
          )}

          {/* ── Per-Generator Controls + Global Color ── */}
          <GenArtControls
            type={config.type}
            params={config.params}
            hueShift={config.hueShift}
            saturation={config.saturation}
            brightness={config.brightness}
            onParamChange={handleParamChange}
            onGlobalChange={handleGlobalChange}
            hideColor={viewFamily === 'normies'}
          />

          {/* ── Darken slider (hidden for Normies) ── */}
          {viewFamily !== 'normies' && (
            <div className="pt-3 border-t border-white/8">
              <DarkenSlider
                label="Darken"
                value={config.darken ?? 0}
                onChange={(v) => update({ darken: v })}
              />
            </div>
          )}
        </div>
      </CollapseTransition>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  CONFIG NORMALIZATION (old SVG → new Canvas2D)
// ═══════════════════════════════════════════════════════════

/**
 * Normalize generative config — convert old SVG format to new Canvas2D format.
 * Returns a complete config with all fields populated.
 */
function normalizeConfig(raw) {
  if (!raw) return { ...DEFAULT_GENART_CONFIG };

  // Already new format
  if (raw.type && GENERATOR_MAP[raw.type]) {
    return {
      enabled: raw.enabled ?? false,
      family: raw.family || GENERATOR_MAP[raw.type].family,
      type: raw.type,
      paletteId: raw.paletteId || getDefaultPalette(GENERATOR_MAP[raw.type].family),
      seed: raw.seed ?? 42,
      hueShift: raw.hueShift ?? 0,
      saturation: raw.saturation ?? 100,
      brightness: raw.brightness ?? 100,
      darken: raw.darken ?? 0,
      params: raw.params || getDefaultParams(raw.type),
    };
  }

  // Old SVG format
  if (raw.preset) {
    const mapping = LEGACY_TYPE_MAP[raw.preset];
    if (mapping) {
      return {
        enabled: raw.enabled ?? false,
        family: mapping.family,
        type: mapping.type,
        paletteId: mapping.paletteId,
        seed: raw.seed ?? 42,
        hueShift: 0,
        saturation: 100,
        brightness: 100,
        darken: raw.darken ?? 0,
        params: getDefaultParams(mapping.type),
      };
    }
  }

  // Unknown — defaults
  return {
    ...DEFAULT_GENART_CONFIG,
    enabled: raw.enabled ?? false,
    seed: raw.seed ?? 42,
    darken: raw.darken ?? 0,
  };
}
